/**
 * Previews for the confirmation of bulk / destructive / restart actions on linear channels: what will be touched,
 * per server, running vs stopped. Pure functions (no React), shared by the page and the Xaccel tab.
 */

interface ChannelServerSummary {
  id: number;
  name: string;
  server_type: string;
  channels_total: number;
  channels_running: number;
}

interface ChannelLike {
  id: number;
  name: string;
  status: string;
  xaccel_stream_enable?: number;
  servers?: ChannelServerSummary[];
}

export type PageAction = 'start-all' | 'stop-all' | 'restart-all' | 'delete-all';

export interface PreviewServer {
  serverId: number;
  name: string;
  type: string;
  running: number;
  stopped: number;
}

/** Page-level Start All acts on the enabled channels not running, Stop / Restart All on the running ones, Delete All on all. */
export function pagePreview(channels: ChannelLike[], action: PageAction): { channels: number; runningChannels: number; names: string[]; servers: PreviewServer[] } {
  const targets = action === 'delete-all' ? channels
    : action === 'start-all' ? channels.filter((c) => c.status !== 'running' && c.xaccel_stream_enable === 1)
    : channels.filter((c) => c.status === 'running');
  const byServer = new Map<number, PreviewServer>();
  for (const ch of targets) {
    for (const s of ch.servers || []) {
      const row = byServer.get(s.id) ?? { serverId: s.id, name: s.name, type: s.server_type, running: 0, stopped: 0 };
      row.running += s.channels_running;
      row.stopped += Math.max(0, s.channels_total - s.channels_running);
      byServer.set(s.id, row);
    }
  }
  return {
    channels: targets.length,
    runningChannels: targets.filter((c) => c.status === 'running').length,
    names: targets.map((c) => c.name),
    servers: [...byServer.values()].filter((s) => s.running + s.stopped > 0),
  };
}

interface LegLike {
  id: number;
  xaccel_server_id: number;
  status: string;
  real_status?: string;
  channel_name: string;
  resolution_name: string;
}

export type LegAction = 'start' | 'stop' | 'delete' | 'delete-db';

/** A leg counts as running by the server's answer when known, else by the DB status. */
export function legIsRunning(l: LegLike): boolean {
  const real = l.real_status;
  return real && real !== 'unknown' ? real === 'running' : l.status === 'running';
}

/** Tab Start = legs not running, Stop = running legs, Delete = every leg; optionally one server only. */
export function legPreview(legs: LegLike[], servers: Array<{ id: number; name: string; server_type?: string }>, action: LegAction, serverId?: number) {
  const scoped = legs.filter((l) => serverId === undefined || l.xaccel_server_id === serverId);
  const targets = scoped.filter((l) => (action === 'start' ? !legIsRunning(l) : action === 'stop' ? legIsRunning(l) : true));
  const byServer = new Map<number, { serverId: number; name: string; type: string; running: number; stopped: number; legs: LegLike[] }>();
  for (const l of targets) {
    const srv = servers.find((s) => s.id === l.xaccel_server_id);
    const row = byServer.get(l.xaccel_server_id) ?? { serverId: l.xaccel_server_id, name: srv?.name || `Server ${l.xaccel_server_id}`, type: srv?.server_type || 'xaccel', running: 0, stopped: 0, legs: [] };
    if (legIsRunning(l)) row.running++; else row.stopped++;
    row.legs.push(l);
    byServer.set(l.xaccel_server_id, row);
  }
  return { total: targets.length, servers: [...byServer.values()] };
}

/** Restart All needs the exact number of running channels typed in. */
export function typedCountConfirms(typed: string, expected: number): boolean {
  return expected > 0 && typed.trim() === String(expected);
}

/** Lines for the legs a delete could not remove on their server (they stay in the list). */
export function deleteFailures(answer: { results?: Array<{ ok: boolean; server: string; resolution: string; channelName: string; error?: string }> }): string[] {
  return (answer.results || []).filter((r) => !r.ok).map((r) => `${r.channelName} (${r.server}): ${r.error || 'error'}`);
}

type TFn = (key: string, params?: Record<string, unknown>) => string;

/**
 * A channel's status message: leg problems are stored by the server as codes + params ({"v":1,"items":[...]}) and
 * rendered here in the UI language; any other text is shown as it is.
 */
export function statusMessageText(msg: string | null | undefined, t: TFn): string {
  if (!msg) return '';
  if (msg.startsWith('{"v":1')) {
    try {
      const parsed = JSON.parse(msg) as { items?: Array<Record<string, unknown> & { code: string }> };
      if (Array.isArray(parsed.items)) return parsed.items.map((it) => t(`linear.statusCode.${it.code}`, it)).join('; ');
    } catch { /* not ours: plain text */ }
  }
  return msg;
}

/** Error codes the linear API sends with a fixed meaning: shown translated; anything else with the server's text. */
const KNOWN_API_ERRORS = new Set(['CONFIRM_REQUIRED', 'CONFIRM_COUNT_MISMATCH', 'INVALID_SERVER_ID', 'SCOPE_REQUIRED', 'INVALID_LEG_IDS', 'SERVER_NOT_OWNED', 'LEGS_NOT_DELETED']);
export function apiErrorText(err: { code?: string; message?: string }, t: TFn): string {
  if (err.code && KNOWN_API_ERRORS.has(err.code)) return t(`linear.apiErrors.${err.code}`);
  return err.message || '';
}

/**
 * A confirmation closes after its action only if it is still the one shown: an action may open a follow-up
 * confirmation (Delete All -> "Rimuovi dal CMS"), which must stay visible.
 */
export function closeAfterRun<T>(started: T, current: T | null): boolean {
  return current !== null && current === started;
}
